import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, ChevronRight, Truck, ShieldCheck, BadgePercent, FileText, ArrowLeft, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CartSheet } from "@/components/CartSheet";
import { ProductCard } from "@/components/ProductCard";
import { ProductImage } from "@/components/ProductImage";
import { Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { useConfig } from "@/context/ConfigContext";
import { fmt } from "@/lib/site";
import { goToQuote } from "@/lib/quote";

export default function ProductDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { addItem, unitPrice, MAYOREO_MIN } = useCart();
    const { hasWhatsApp, waHref } = useConfig();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [qty, setQty] = useState(1);
    const [activeImg, setActiveImg] = useState(0);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        setQty(1);
        setActiveImg(0);
        api.get(`/products/${id}`)
            .then((r) => {
                setProduct(r.data);
                return api.get("/products", { params: { categoria: r.data.categoria, limit: 12 } });
            })
            .then((r) => setRelated(r?.data?.items || []))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id]);

    const add = () => {
        addItem(product, qty);
        toast.success("Agregado al carrito", { description: `${qty}× ${product.nombre}` });
    };

    // Envía al formulario de presupuesto con el producto prellenado
    const pedirPresupuesto = () => {
        goToQuote(nav, {
            detalle: `${qty}× ${product.nombre} (${product.sku})`,
            tipo: qty >= MAYOREO_MIN ? "mayoreo" : "detal",
            producto: product.nombre,
            producto_sku: product.sku,
            producto_precio: fmt(unitPrice(product, qty)),
        });
    };

    // Botón WhatsApp inteligente: abre WhatsApp si hay número; si no, abre el formulario
    const consultarWhatsApp = () => {
        if (hasWhatsApp) {
            const href = waHref(`Hola, me interesa: ${product.nombre} (código ${product.sku}) - ${fmt(unitPrice(product, qty))}. ¿Está disponible?`);
            window.open(href, "_blank");
        } else {
            pedirPresupuesto();
        }
    };

    if (loading) {
        return (
            <div className="bg-crema min-h-screen">
                <Navbar />
                <div className="pt-28 md:pt-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
                    <div className="aspect-square bg-secondary rounded-3xl animate-pulse" />
                    <div className="space-y-4">
                        <div className="h-6 w-32 bg-secondary rounded-full animate-pulse" />
                        <div className="h-10 w-full bg-secondary rounded-lg animate-pulse" />
                        <div className="h-10 w-40 bg-secondary rounded-lg animate-pulse" />
                        <div className="h-24 w-full bg-secondary rounded-lg animate-pulse" />
                    </div>
                </div>
            </div>
        );
    }

    if (notFound || !product) {
        return (
            <div className="bg-crema min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pt-24" data-testid="product-not-found">
                    <p className="font-display font-bold text-3xl text-ink">Producto no encontrado</p>
                    <p className="text-sm text-mut mt-2">Puede que ya no esté disponible.</p>
                    <Link to="/tienda" className="mt-6 bg-verde text-white rounded-full px-7 py-3 text-sm font-medium hover:bg-verde-800 transition-colors">
                        Volver a la tienda
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const may = qty >= MAYOREO_MIN;
    const u = unitPrice(product, qty);
    const gallery = [product.foto_url, ...(product.fotos || [])].filter((v, i, a) => v && a.indexOf(v) === i);
    const mainImg = gallery[activeImg] || gallery[0] || product.foto_url;
    const specs = [
        { k: "SKU", v: product.sku },
        { k: "Marca", v: product.marca },
        { k: "Categoría", v: product.categoria },
        { k: "Subcategoría", v: product.subcategoria },
        { k: "Condición", v: product.condicion || "Nuevo" },
    ].filter((s) => s.v);

    const otros = related.filter((p) => p.id !== product.id).slice(0, 4);

    return (
        <div className="bg-crema min-h-screen" data-testid="product-detail-page">
            <Navbar />
            <CartSheet />

            <div className="pt-20 md:pt-24">
                {/* Breadcrumb */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <nav className="flex items-center gap-1.5 text-xs text-mut flex-wrap" data-testid="breadcrumb">
                        <Link to="/" className="hover:text-verde">Inicio</Link>
                        <ChevronRight size={13} />
                        <Link to="/tienda" className="hover:text-verde">Tienda</Link>
                        <ChevronRight size={13} />
                        <Link to={`/tienda?categoria=${encodeURIComponent(product.categoria)}`} className="hover:text-verde line-clamp-1">{product.categoria}</Link>
                    </nav>
                </div>

                {/* Main */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 grid md:grid-cols-2 gap-8 lg:gap-14 items-start">
                    {/* Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                        className="md:sticky md:top-28"
                    >
                        <div className="relative aspect-square bg-white border border-linea rounded-3xl overflow-hidden shadow-soft group">
                            <ProductImage
                                key={mainImg}
                                src={mainImg}
                                alt={product.nombre}
                                className="w-full h-full object-contain p-8 md:p-12 transition-transform duration-500 ease-out group-hover:scale-110"
                            />
                            {(product.etiqueta || product.destacado) && (
                                <span className={`absolute top-5 left-5 text-white text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full font-bold ${product.etiqueta ? "bg-naranja" : "bg-madera"}`}>
                                    {product.etiqueta || "Destacado"}
                                </span>
                            )}
                        </div>

                        {gallery.length > 1 && (
                            <div className="mt-4 grid grid-cols-5 gap-3" data-testid="product-gallery-thumbs">
                                {gallery.map((g, i) => (
                                    <button
                                        key={g}
                                        onClick={() => setActiveImg(i)}
                                        data-testid={`gallery-thumb-${i}`}
                                        aria-label={`Ver foto ${i + 1}`}
                                        className={`relative aspect-square bg-white border rounded-xl overflow-hidden transition-colors ${
                                            i === activeImg ? "border-naranja ring-2 ring-naranja/30" : "border-linea hover:border-madera"
                                        }`}
                                    >
                                        <ProductImage src={g} alt={`${product.nombre} ${i + 1}`} className="w-full h-full object-contain p-2" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Info */}
                    <div>
                        <button onClick={() => nav(-1)} data-testid="back-btn" className="flex items-center gap-1.5 text-xs text-mut hover:text-verde mb-4 transition-colors">
                            <ArrowLeft size={14} /> Volver
                        </button>

                        <span className="text-[11px] uppercase tracking-[0.2em] text-madera font-semibold">
                            {product.marca} · {product.sku}
                        </span>
                        <h1 data-testid="product-title" className="font-display font-extrabold text-3xl md:text-4xl text-ink mt-2 leading-tight tracking-tight">
                            {product.nombre}
                        </h1>

                        {/* Price */}
                        <div className="mt-6 flex items-end gap-4 flex-wrap">
                            <div>
                                <p className="text-[11px] uppercase tracking-wide text-mut">Precio {may ? "mayoreo" : "detal"}</p>
                                <p data-testid="product-price" className="font-display font-bold text-4xl md:text-5xl text-verde">{fmt(u)} <span className="text-base font-sans font-semibold text-mut">+ IVA</span></p>
                                {(product.unidad_venta || product.rendimiento) && (
                                    <p data-testid="product-unidad" className="text-sm font-semibold text-ink mt-1">
                                        {product.unidad_venta}{product.unidad_venta && product.rendimiento ? " · " : ""}{product.rendimiento}
                                    </p>
                                )}
                            </div>
                            {may && (
                                <p className="text-sm text-mut line-through mb-2">{fmt(product.precio_usd)}</p>
                            )}
                        </div>
                        <div className="mt-2 inline-flex items-center gap-2 bg-madera/10 text-madera-dark text-xs font-medium px-3 py-1.5 rounded-full">
                            <BadgePercent size={14} />
                            Mayoreo {fmt(product.precio_mayoreo)} desde {MAYOREO_MIN} uds.
                        </div>

                        {/* Qty + actions */}
                        <div className="mt-7 flex flex-wrap items-center gap-4">
                            <div className="flex items-center border border-linea rounded-full bg-white h-12">
                                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="px-4 h-full text-mut hover:text-naranja"><Minus size={16} /></button>
                                <span data-testid="qty-value" className="w-10 text-center font-semibold text-ink">{qty}</span>
                                <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="px-4 h-full text-mut hover:text-naranja"><Plus size={16} /></button>
                            </div>
                            <button
                                onClick={add}
                                data-testid="detail-add-to-cart"
                                className="flex-1 min-w-[180px] h-12 bg-naranja text-white rounded-full px-7 font-semibold text-sm hover:bg-naranja-dark transition-colors flex items-center justify-center gap-2"
                            >
                                <ShoppingBag size={18} /> Agregar al carrito
                            </button>
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                onClick={consultarWhatsApp}
                                data-testid="detail-whatsapp-btn"
                                className="h-12 bg-[#25D366] text-white rounded-full font-semibold text-sm hover:brightness-95 transition-all flex items-center justify-center gap-2"
                            >
                                <MessageCircle size={17} /> Consultar por WhatsApp
                            </button>
                            <button
                                onClick={pedirPresupuesto}
                                data-testid="detail-quote-btn"
                                className="h-12 border border-verde text-verde rounded-full font-semibold text-sm hover:bg-verde hover:text-white transition-colors flex items-center justify-center gap-2"
                            >
                                <FileText size={17} /> Pedir presupuesto
                            </button>
                        </div>

                        {/* Trust */}
                        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                            {[
                                { icon: ShieldCheck, t: "Producto nuevo" },
                                { icon: Truck, t: "Envío nacional" },
                                { icon: BadgePercent, t: "Precio por volumen" },
                            ].map((f) => (
                                <div key={f.t} className="bg-hueso border border-linea rounded-xl py-3 px-2">
                                    <f.icon size={18} className="mx-auto text-verde mb-1.5" strokeWidth={1.8} />
                                    <p className="text-[11px] text-mut leading-tight">{f.t}</p>
                                </div>
                            ))}
                        </div>

                        {/* Description */}
                        {product.descripcion && (
                            <div className="mt-8">
                                <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-2">Descripción</h3>
                                <p className="text-sm text-ink/80 leading-relaxed whitespace-pre-line">{product.descripcion}</p>
                            </div>
                        )}

                        {/* Specs */}
                        <div className="mt-8">
                            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-3">Especificaciones</h3>
                            <dl className="divide-y divide-linea border border-linea rounded-2xl overflow-hidden" data-testid="product-specs">
                                {specs.map((s) => (
                                    <div key={s.k} className="flex justify-between gap-4 px-4 py-3 bg-hueso/60">
                                        <dt className="text-sm text-mut">{s.k}</dt>
                                        <dd className="text-sm font-medium text-ink text-right">{s.v}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Related */}
                {otros.length > 0 && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                        <Reveal>
                            <div className="flex items-end justify-between mb-8">
                                <div>
                                    <span className="text-xs uppercase tracking-[0.2em] text-madera font-semibold">También te puede servir</span>
                                    <h2 className="font-display font-extrabold text-3xl md:text-4xl text-ink mt-1 tracking-tight">Productos relacionados</h2>
                                </div>
                                <Link to={`/tienda?categoria=${encodeURIComponent(product.categoria)}`} className="hidden sm:inline text-sm font-medium text-verde hover:text-naranja transition-colors">
                                    Ver más →
                                </Link>
                            </div>
                        </Reveal>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5" data-testid="related-products">
                            {otros.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}
